<script lang="ts">
  import type { Alert } from '../../api/types'
  import type { AlertTone } from '../../forecast/tone'
  import { capIdOf, untilLabel } from '../../forecast/alerts'
  import { alertTone } from '../../forecast/tone'
  import { requestAlert } from '../../state/alertLink.svelte'
  import Icon from '../Icon.svelte'

  /**
   * One row per active alert, directly under the status line (Phase 10 plan
   * §4.5). It replaced the Alerts panel, which could be closed; a row cannot,
   * so today's warning is never behind a tap somebody made last week.
   *
   * A tap opens the same `AlertPopup` a notification or a map tap opens,
   * through `alertLink`, carrying the bundle's copy so the text is on screen
   * at once. A row whose id is not a CAP id is drawn without a button and
   * asks for nothing.
   */

  let { alerts }: { alerts: Alert[] } = $props()

  let rows = $derived(
    alerts.map((alert, i) => ({
      alert,
      key: alert.id ?? `${i}:${alert.event ?? ''}`,
      capId: capIdOf(alert.id),
      tone: alertTone(alert.event, alert.description),
      until: untilLabel(alert.expires),
    })),
  )

  function open(capId: string, alert: Alert) {
    // The popup is fixed over the whole app, so on a phone it opens over
    // Forecast with the map still hidden behind it.
    requestAlert(capId, alert)
  }
</script>

{#if rows.length > 0}
  <ul class="alerts" aria-label="Alerts">
    {#each rows as row (row.key)}
      <li>
        {#if row.capId}
          {@const capId = row.capId}
          <button type="button" class="row touch-target" onclick={() => open(capId, row.alert)}>
            {@render body(row.alert, row.tone, row.until, true)}
          </button>
        {:else}
          <div class="row">{@render body(row.alert, row.tone, row.until, false)}</div>
        {/if}
      </li>
    {/each}
  </ul>
{/if}

{#snippet body(alert: Alert, tone: AlertTone, until: string, opens: boolean)}
  <span class="mark" data-tone={tone}><Icon name="triangle-alert" size={22} /></span>
  <span class="text">
    <span class="event">{alert.event ?? 'Alert'}</span>
    {#if until}<span class="until">{until}</span>{/if}
  </span>
  {#if opens}<span class="chev"><Icon name="chevron-right" size={16} strokeWidth={2.5} /></span>{/if}
{/snippet}

<style>
  /* Phase 11: inside the gutter, 3 px rules between the rows, and the tier
     as a 56 px block down the left. */
  .alerts {
    list-style: none;
    margin: 14px var(--gutter, 0px) 0;
    padding: 0;
    border-top: 3px solid var(--color-rule);
  }

  li {
    border-bottom: 3px solid var(--color-rule);
  }

  .row {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    align-items: stretch;
    width: 100%;
    min-height: 72px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-text);
    text-align: left;
    font: inherit;
  }

  button.row {
    cursor: pointer;
  }

  @media (hover: hover) {
    button.row:hover {
      background: color-mix(in srgb, var(--color-text) 7%, transparent);
    }
  }

  /* The alert's tier of the condition palette, by the event name's last word
     (the push poller's rule): yellow short of a Warning, red for one, deep
     red for an emergency. The header above takes the highest of them. */
  .mark {
    display: grid;
    place-items: center;
    background: var(--color-field);
    color: var(--color-on-field);
  }

  .text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    min-width: 0;
    padding: 12px 0 12px 16px;
  }

  .event {
    font-size: 19px;
    font-weight: 800;
    line-height: 1.15;
  }

  .until {
    font-size: 15px;
    color: var(--color-muted);
  }

  .chev {
    display: grid;
    place-items: center;
    padding: 0 4px 0 10px;
  }

  @media (hover: hover) {
    button.row:hover .event {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
</style>
